import { Route } from "react-router-dom";
import { useEffect, useState } from "react";
import { IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { useAuth } from "./hooks/useAuthStore";
import { AuthListener } from "./components/AuthListener";
import { AuthenticatedRoutes } from "./components/AuthenticatedRoutes";
import { OnboardingGuard } from "./components/OnboardingGuard";
import TabsLayout from "./components/TabsLayout";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./components/ToastProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NativeAppReady } from "./components/NativeAppReady";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { ChatFAB } from "./components/chat/ChatFAB";
import { ChatDrawer } from "./components/chat/ChatDrawer";
import { VoiceOverlay } from "./components/chat/VoiceOverlay";
import { WelcomeBackPrompt } from "./components/chat/WelcomeBackPrompt";
import { PlanReadyWatcher } from "./components/PlanReadyWatcher";

// Pages
import LoginPage from "./pages/LoginPage";
import OnboardingPage from "./pages/OnboardingPage";
import InvitePage from "./pages/InvitePage";
import OAuthConsentPage from "./pages/OAuthConsentPage";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables and custom styles */
import "./theme/variables.css";
import "./index.css";

// Setup Ionic
setupIonicReact();

function AppContent() {
  const { initialized } = useAuth();
  // Enable Ionic route animations only on initial mount to avoid flashing on subsequent navigations
  const [routeAnimationsEnabled, setRouteAnimationsEnabled] =
    useState<boolean>(true);

  useEffect(() => {
    // Disable after first paint so later navigations do not animate
    const timer = window.setTimeout(() => setRouteAnimationsEnabled(false), 0);
    return () => window.clearTimeout(timer);
  }, []);

  if (!initialized) {
    return (
      <IonApp>
        <AuthListener />
        <LoadingSpinner />
      </IonApp>
    );
  }

  return (
    <IonApp>
      <AuthListener />
      <ToastProvider />
      <IonReactRouter>
        <IonRouterOutlet animated={routeAnimationsEnabled}>
          <Route path="/login" exact component={LoginPage} />
          <Route path="/onboarding" exact component={OnboardingPage} />
          <Route path="/invite" exact component={InvitePage} />
          <Route path="/oauth/consent" exact component={OAuthConsentPage} />
          <AuthenticatedRoutes>
            <OnboardingGuard>
              <Route component={TabsLayout} />
            </OnboardingGuard>
          </AuthenticatedRoutes>
        </IonRouterOutlet>
        <PlanReadyWatcher />
        <ChatFAB />
        <ChatDrawer />
        <VoiceOverlay />
        <WelcomeBackPrompt />
      </IonReactRouter>
    </IonApp>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <NativeAppReady />
        <AppContent />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
